// src/Dashboard/pages/user/Lexique/utils/lexiqueUtils.js

export const DEFAULT_VISIBLE_ROWS = 120;

export const DEFAULT_UI_STATE = {
  q: "",
  letter: "Toutes",
  sort: "jp", // jp | fr
  favOnly: false,
  perPage: DEFAULT_VISIBLE_ROWS,
};

export const UI_STORAGE_KEY = "lexique_dashboard_ui_state";
export const FAV_STORAGE_KEY = "lexique_favorites";

export const collatorFR = new Intl.Collator("fr", { sensitivity: "base" });

export const strip = (value = "") =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

export const readStorageJson = (key, fallback) => {
  try {
    if (typeof window === "undefined") return fallback;

    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const writeStorageJson = (key, value) => {
  try {
    if (typeof window === "undefined") return;
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};

export const isKarateDiscipline = (discipline = "") => {
  return strip(discipline).includes("karate");
};

export const getJapanese = (row) => {
  return String(row?.japonais ?? row?.terme_japonais ?? row?.jp ?? "").trim();
};

export const getTranslation = (row) => {
  return String(row?.traduction ?? row?.francais ?? row?.fr ?? "").trim();
};

export const getLexiqueKey = (row) => {
  return `${strip(getJapanese(row))}__${strip(getTranslation(row))}`;
};

export const getInitialLexiqueUi = () => {
  const saved = readStorageJson(UI_STORAGE_KEY, null);
  let base = saved ? { ...DEFAULT_UI_STATE, ...saved } : DEFAULT_UI_STATE;

  try {
    if (typeof window === "undefined") return base;

    const params = new URLSearchParams(window.location.search);

    if (params.has("q")) {
      base = { ...base, q: params.get("q") || "" };
    }

    if (params.has("letter")) {
      base = { ...base, letter: params.get("letter") || "Toutes" };
    }

    if (params.has("sort")) {
      const sort = params.get("sort");
      base = {
        ...base,
        sort: ["jp", "fr"].includes(sort) ? sort : DEFAULT_UI_STATE.sort,
      };
    }

    if (params.has("fav")) {
      base = { ...base, favOnly: params.get("fav") === "1" };
    }

    return {
      ...base,
      perPage: DEFAULT_VISIBLE_ROWS,
    };
  } catch {
    return base;
  }
};

export const buildShareUrl = (ui) => {
  if (typeof window === "undefined") return "";

  const params = new URLSearchParams();

  if (ui.q) params.set("q", ui.q);
  if (ui.letter && ui.letter !== "Toutes") params.set("letter", ui.letter);
  if (ui.sort && ui.sort !== DEFAULT_UI_STATE.sort) params.set("sort", ui.sort);
  if (ui.favOnly) params.set("fav", "1");

  const query = params.toString();

  return `${window.location.origin}${window.location.pathname}${
    query ? `?${query}` : ""
  }`;
};

export const escapeCsvValue = (value) => {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
};

export const makeLexiqueCsv = (rows = []) => {
  const lines = [
    ["japonais", "traduction"],
    ...rows.map((row) => [getJapanese(row), getTranslation(row)]),
  ];

  return lines.map((line) => line.map(escapeCsvValue).join(";")).join("\n");
};

export const sanitizeFilename = (value = "lexique") => {
  return strip(value)
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "");
};

export const hasJPChars = (value = "") => {
  return /[\u3040-\u30FF\u4E00-\u9FFF]/.test(value);
};

export const pickVoice = (langStartsWith = "ja", fallbackLang = "ja-JP") => {
  if (typeof window === "undefined") {
    return { voice: null, lang: fallbackLang };
  }

  const synth = window.speechSynthesis;
  if (!synth) return { voice: null, lang: fallbackLang };

  const voices = synth.getVoices ? synth.getVoices() : [];
  const voice = voices.find((item) =>
    item.lang?.toLowerCase().startsWith(langStartsWith)
  );

  return {
    voice: voice || null,
    lang: voice?.lang || fallbackLang,
  };
};

export const speakWith = (langStartsWith, text, fallbackLang) => {
  try {
    if (typeof window === "undefined") return;

    const synth = window.speechSynthesis;
    if (!synth || !text) return;

    synth.cancel();

    const { voice, lang } = pickVoice(langStartsWith, fallbackLang);
    const utterance = new SpeechSynthesisUtterance(text);

    if (voice) utterance.voice = voice;

    utterance.lang = lang;
    utterance.rate = 1;
    utterance.pitch = 1;

    synth.speak(utterance);
  } catch {}
};